/**
 * @fileoverview HotelProfileOverlay.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 */
import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import DynamicImage from '../ui/DynamicImage'

function HotelProfileOverlay({ isOpen, onClose, hotel }) {
    const { t } = useTranslation();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Reset index when hotel changes
    useEffect(() => {
        if (isOpen) {
            setCurrentImageIndex(0);
            document.body.style.overflow = 'hidden';
            window.__lenis?.stop();
        } else {
            document.body.style.overflow = 'unset';
            window.__lenis?.start();
        }
        return () => {
            document.body.style.overflow = 'unset';
            window.__lenis?.start();
        }
    }, [isOpen, hotel]);

    if (!hotel) return null;

    const images = hotel.images || [];

    const handleNext = (e) => {
        e.stopPropagation();
        if (images.length > 1) {
            setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }
    }

    const handlePrev = (e) => {
        e.stopPropagation();
        if (images.length > 1) {
            setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
        }
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <div className='fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8'>
                    {/* Backdrop */}
                    <motion.div 
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={onClose}
                        className='absolute inset-0 bg-black/60 backdrop-blur-sm'
                    />

                    {/* Modal */}
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className='relative w-full max-w-6xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row'
                    >
                        {/* Close button */}
                        <button 
                            onClick={onClose}
                            className='absolute top-4 right-4 z-20 w-10 h-10 bg-white/80 backdrop-blur hover:bg-white text-gray-800 rounded-full flex items-center justify-center transition-colors shadow-sm'
                        >
                            <i className='fi fi-br-cross text-xs'></i>
                        </button>

                        {/* Image Slider */}
                        <div className='w-full md:w-2/3 aspect-[3/2] relative bg-gray-100 shrink-0 overflow-hidden'>
                            {images.length > 0 ? (
                                <>
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={currentImageIndex}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className='w-full h-full absolute inset-0'
                                        >
                                            <DynamicImage 
                                                image={images[currentImageIndex]} 
                                                alt={hotel.name}
                                                className='w-full h-full object-cover'
                                            />
                                        </motion.div>
                                    </AnimatePresence>
                                    
                                    {images.length > 1 && (
                                        <>
                                            <button onClick={handlePrev} className='absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white text-gray-800 rounded-full flex items-center justify-center shadow-md transition-all'>
                                                <i className='fi fi-br-angle-left mt-1'></i>
                                            </button>
                                            <button onClick={handleNext} className='absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white text-gray-800 rounded-full flex items-center justify-center shadow-md transition-all'>
                                                <i className='fi fi-br-angle-right mt-1'></i>
                                            </button>
                                            
                                            {/* Indicators */}
                                            <div className='absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2'>
                                                {images.map((_, idx) => (
                                                    <div 
                                                        key={idx} 
                                                        className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIndex ? 'bg-white scale-125' : 'bg-white/50'}`}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : (
                                <div className='w-full h-full flex items-center justify-center text-gray-400'>
                                    <i className='fi fi-br-picture text-5xl'></i>
                                </div>
                            )}
                        </div>

                        {/* Details */}
                        <div className='w-full md:w-1/3 p-6 md:p-8 flex flex-col gap-5 overflow-y-auto no-scrollbar md:absolute md:right-0 md:top-0 md:h-full z-10'>
                            <div className='flex flex-col gap-2 shrink-0 pr-8'>
                                {hotel.classification && (
                                    <span className='inline-block w-max bg-amber-50 text-amber-600 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest'>
                                        {hotel.classification}
                                    </span>
                                )}
                                <h2 className='text-2xl md:text-3xl font-bold text-gray-800 leading-tight'>{hotel.name}</h2>
                                <div className='flex items-center gap-2 text-gray-500 font-medium text-sm md:text-base'>
                                    <i className='fi fi-br-map-marker text-KITH-primary'></i>
                                    <span>{hotel.city && hotel.location ? `${hotel.city}, ${hotel.location}` : (hotel.city || hotel.location || t('hotels.unknown_location'))}</span>
                                </div>
                            </div>
                            
                            {hotel.description ? (
                                <div className='prose prose-sm text-gray-600 max-w-none text-justify'>
                                    <p>{hotel.description}</p>
                                </div>
                            ) : (
                                <p className='text-gray-400 italic text-sm'>{t('hotels.no_description')}</p>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    )
}

export default HotelProfileOverlay
